import { Injectable, inject } from "@angular/core";

import { MatDialog, MatDialogConfig } from "@angular/material/dialog";

import { IShellService } from "@tylertech/incode-module";
import { IAggregateActionHandler, AggregateHelperService } from "@tylertech/aggregates-module";
import { IAggregate, ICardData, ICardAction, ExecutionResult } from "@tylertech/tyler-api-types";

import
{
    ServiceRequestTextEmailDialogComponent, ServiceRequestTextEmailDialogData,
    ServiceRequestRejectDialogComponent, ServiceRequestRejectDialogData,
    ContactMatchDialogComponent, ContactMatchDialogData
} from "../components";

@Injectable()
export class UtilityServiceRequestActionHandler implements IAggregateActionHandler
{
    private readonly $aggregateHelperService = inject(AggregateHelperService);
    private readonly $shellService = inject(IShellService);
    private readonly matDialog = inject(MatDialog);


    predicate(aggregate: IAggregate, _cardData: ICardData, _action: ICardAction): boolean
    {
        return this.isServiceConnectRequest(aggregate) || this.isServiceDisconnectRequest(aggregate);
    }

    private isServiceConnectRequest(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.ConnectServiceRequests";
    }

    private isServiceDisconnectRequest(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.DisconnectServiceRequests";
    }

    execute(aggregate: IAggregate, cardData: ICardData, action: ICardAction): Promise<boolean>
    {
        const requestType = this.isServiceConnectRequest(aggregate) ? "Connect" : "Disconnect";
        const requestId = this.$aggregateHelperService.getFieldValue(cardData, "Id");
        if (action.Id === "Reject")
        {
            return this.rejectServiceRequest(aggregate, cardData, action, requestType, requestId);
        }
        else
        {
            return this.acceptServiceRequest(aggregate, cardData, action, requestType);
        }
    }

    private acceptServiceRequest(aggregate: IAggregate, cardData: ICardData, action: ICardAction, requestType: string): Promise<boolean>
    {

        const requestId = this.$aggregateHelperService.getFieldValue(cardData, "Id");
        if (!action.content)
        {
            this.createEmail(aggregate, cardData, action, `${requestType} Service Order Accepted`);
        }

        return this.acceptDisconnectOrConnectRequest(requestType, requestId, aggregate, cardData, action);
    }

    private acceptDisconnectOrConnectRequest(requestType: string, requestId: string, aggregate: IAggregate, cardData: ICardData, action: ICardAction): Promise<boolean>
    {
        if (requestType === "Disconnect")
        {
            return this.$shellService.openShellFunction<ICardAction>(
                action.Id, requestId, false, `Failed to ${requestType}.`)
                .then(
                    (result: ExecutionResult<ICardAction>) =>
                    {
                        return result.Success
                            && this.showTextEmailDialog(cardData, action, requestId, requestType, true);
                    },
                    () => false
                );
        }
        else
        {
            return this.showContactMatchDialog(this.getField(cardData, "Id"))
                .then(
                    (contactId: string) =>
                    {
                        if (!contactId)
                        {
                            return false;
                        }

                        return this.$shellService.openShellFunction<ICardAction>(
                            action.Id,
                            JSON.stringify([requestId, contactId]),
                            false,
                            `Failed to ${requestType}.`)
                            .then(() => true, () => false);
                    },
                    () => { /* Squashing Rejected Promise */ })
                .then((result: boolean) =>
                {
                    return result && this.showTextEmailDialog(cardData, action, requestId, requestType, true);
                });
        }
    }

    private showContactMatchDialog(requestId: string): Promise<string>
    {
        const dialogConfig: MatDialogConfig<ContactMatchDialogData> = {
            data: {
                requestId
            }
        };
        const dialogRef = this.matDialog.open<ContactMatchDialogComponent, ContactMatchDialogData, string>(
            ContactMatchDialogComponent,
            dialogConfig);
        return dialogRef.afterClosed()
            .toPromise();
    }

    private rejectServiceRequest(
        aggregate: IAggregate, cardData: ICardData, action: ICardAction, requestType: string, requestId: string): Promise<boolean>
    {
        if (action.content)
        {
            return this.showServiceRequestTextEmailDialog(cardData, action, requestType, requestId)
                .then(
                    (r: boolean) =>
                    {
                        if (!r)
                        {
                            return false;
                        }

                        return this.showServiceRequestRejectDialog(this.getField(cardData, "Id"))
                            .catch(() => false);
                    },
                    () => false
                );
        }
        else
        {
            this.createEmail(aggregate, cardData, action, `${requestType} Service Order Rejected`);

            return this.showServiceRequestRejectDialog(this.getField(cardData, "Id"))
                .then(
                    () => true,
                    () => false
                );
        }
    }

    private showServiceRequestTextEmailDialog(
        cardData: ICardData, cardAction: ICardAction, requestType: string, requestId: string, isAccepted: boolean = false): Promise<boolean>
    {
        const dialogConfig: MatDialogConfig<ServiceRequestTextEmailDialogData> = {
            data: {
                cardData,
                cardAction,
                requestId,
                requestType,
                isAccepted,
            }
        };
        const dialogRef = this.matDialog.open<ServiceRequestTextEmailDialogComponent, ServiceRequestTextEmailDialogData, boolean>(
            ServiceRequestTextEmailDialogComponent,
            dialogConfig);

        return dialogRef.afterClosed()
            .toPromise();
    }

    private showServiceRequestRejectDialog(requestId: string): Promise<boolean>
    {
        const dialogConfig: MatDialogConfig<ServiceRequestRejectDialogData> = {
            data: {
                requestId
            }
        };

        const dialogRef = this.matDialog.open<ServiceRequestRejectDialogComponent, ServiceRequestRejectDialogData, boolean>(
            ServiceRequestRejectDialogComponent, dialogConfig);
        return dialogRef.afterClosed()
            .toPromise();
    }

    private showTextEmailDialog(cardData: ICardData, action: ICardAction, requestId: string, requestType: string, isAccepted: boolean): Promise<boolean>
    {
        return this.showServiceRequestTextEmailDialog(cardData, action, requestType, requestId, isAccepted)
            .catch(() => false);
    }

    private createEmail(aggregate: IAggregate, cardData: ICardData, _action: ICardAction, _requestType: string): void
    {
        // TODO - use EmailUtilities from @tylertech/shared-module

        let email = this.$aggregateHelperService.getFieldValue(cardData, "Email");
        email = encodeURIComponent(!email ? "" : email);

        let emailSubject = this.$aggregateHelperService.getFieldValue(cardData, "EmailSubject");
        emailSubject = encodeURIComponent(!emailSubject ? aggregate.Name : emailSubject);

        let emailBody = this.$aggregateHelperService.getFieldValue(cardData, "EmailBody");
        emailBody = encodeURIComponent(!emailBody ? "" : emailBody);

        window.location.href = `mailto:${email}?Subject=${emailSubject}&body=%0D%0A${emailBody}`;
    }

    private getField(cardData: ICardData, fieldName: string): string
    {
        return this.$aggregateHelperService.getFieldValue(cardData, fieldName);
    }

    load(_aggregate: IAggregate, _cardData: ICardData, _action: ICardAction): void
    {
        /* NO_OP */
    }
}
